import { Badge, Card, Flex, Stack, Text, Title } from "@mantine/core";
import { ArrowRightIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import type { IAmendmentHistoryItem } from "../../apis/invoice";
import { currencyFormat } from "../../utils";
import { dateTimeFormat } from "../../utils/date";

const STATUS_COLOR: Record<IAmendmentHistoryItem["status"], string> = {
  ACCEPTED: "green",
  REJECTED: "red",
  WITHDRAWN: "gray",
};

type AmendmentHistoryProps = {
  items: IAmendmentHistoryItem[];
};

export function AmendmentHistory({ items }: AmendmentHistoryProps) {
  const { t } = useTranslation("invoice");

  if (items.length === 0) return null;

  return (
    <Card withBorder radius="md" p="md">
      <Title order={5} mb="sm">
        {t("amend.historyTitle")}
      </Title>
      <Stack gap="sm">
        {items.map((item) => (
          <Stack key={item.id} gap={4}>
            <Flex align="center" gap="xs" wrap="wrap">
              <Text size="sm" fw={600} ff="monospace">
                {item.refCode}
              </Text>
              <Badge size="sm" variant="light" color={STATUS_COLOR[item.status] ?? "gray"}>
                {t(`amend.status.${item.status}`, { defaultValue: item.status })}
              </Badge>
              <Text size="xs" c="dimmed">
                {dateTimeFormat(item.respondedAt ?? item.proposedAt)}
              </Text>
            </Flex>
            <Flex align="center" gap="xs" wrap="wrap">
              <Text size="sm">{currencyFormat(item.previousTotal)}</Text>
              <ArrowRightIcon size={12} />
              <Text size="sm" fw={500}>
                {currencyFormat(item.newTotal)}
              </Text>
            </Flex>
            <Text size="xs" c="dimmed">
              {item.reason}
            </Text>
            {item.status === "REJECTED" && item.rejectionReason && (
              <Text size="xs" c="red.8">
                {t("amend.rejectionReason")}: {item.rejectionReason}
              </Text>
            )}
          </Stack>
        ))}
      </Stack>
    </Card>
  );
}
